package com.siriusxm.pia.views.unifiedaggregator.events

import androidx.compose.runtime.*
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.entityId
import com.siriusxm.pia.rest.unifiedaggregator.entityType
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.smithy.SmithyViewOptions
import com.siriusxm.pia.views.unifiedaggregator.smithy.smithyEntityView
import com.siriusxm.pia.views.unifiedaggregator.smithy.smithyTableView
import com.siriusxm.smithy4kt.SmithyMember
import com.siriusxm.unifiedcontent.console.aggregator.intakeErrorMessage
import com.siriusxm.smithy4kt.query
import com.siriusxm.smithy4kt.resolveJsonPath
import unifiedcontent.aggregator.AuditContext
import unifiedcontent.aggregator.ContentUpdateType
import unifiedcontent.aggregator.PartialUpdate
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonObject
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * Renders a partial event.
 * @param onDelete if set, the view will include controls to allow the viewer to send a REMOVE event for this
 * partial. The callback will be invoked if the REMOVE has taken place. Pass it only when the viewer
 * may remove this partial (see `rememberPublishablePartials`); the aggregator checks again on submit.
 */
@Composable
fun partialEventView(
    aggregator: AggregatorService,
    partial: PartialUpdate,
    onDelete: (suspend () -> Unit)? = null
) {
    jsonView(partial.modification.jsonObject, true, object : DefaultJsonViewRenderer(true) {
        @Composable
        override fun renderRoot(element: JsonElement) {
            var deleting by remember { mutableStateOf(false) }

            Div({
                style {
                    position(Position.Relative)
                    padding(5.px)
                }
            }) {
                if (onDelete != null) {
                    Div({
                        style {
                            position(Position.Absolute)
                            top(5.px)
                            right(5.px)
                        }
                    }) {
                        icon("delete") {
                            title = "Delete this partial"
                            action {
                                deleting = true
                            }
                        }
                    }
                }

                detailGrid {
                    detail(
                        "Partial",
                        aggregator.partial(partial.partialId)?.name
                            ?: partial.partialId
                    )

                    partial.modification.entityId?.let { id ->
                        detail("Entity") {
                            EntityLink(id)
                        }
                    }

                    partial.auditContext?.author?.let {
                        detail("Author", it)
                    }
                    partial.producer?.let { producerId ->
                        aggregator.producerById(producerId)?.name ?: producerId
                    }?.let {
                        detail("Producer", it)
                    }
                }
            }
            Div {
                val entity = partial.modification
                val model = aggregator.entityModel
                val shape = entity.entityType?.let {
                    aggregator.entityType(it)
                }?.shapeId?.let {
                    model?.getShape(it)
                }

                if (!partial.operations.isNullOrEmpty()) {
                    partial.operations?.forEach { operation ->
                        Div {
                            H4({
                                style { margin(0.px, 5.px) }
                            }) {
                                Text(operation.type.name)
                            }
                            val targetShape = shape?.resolveJsonPath(operation.targetPath)
                            val sourceData = (operation.sourcePath ?: operation.targetPath).let {
                                partial.modification.query(it)
                            }.firstOrNull()

                            val viewerOption = SmithyViewOptions()
                            if (targetShape != null && sourceData != null) {
                                if (targetShape is SmithyMember) {
                                    smithyTableView(
                                        listOf(targetShape.name to targetShape),
                                        viewerOption,
                                        buildJsonObject {
                                            put(targetShape.name, sourceData)
                                        }
                                    )
                                } else {
                                    smithyEntityView(targetShape, sourceData)
                                }
                            } else {
                                super.renderRoot(partial.modification)
                            }
                        }
                    }
                } else if (shape != null) {
                    smithyEntityView(shape, partial.modification)
                } else {
                    super.renderRoot(partial.modification)
                }
            }

            if (deleting) {
                confirmationDialog(
                    "Delete this partial?",
                    "Are you sure you'd like to remove this partial? There is no way to undo this. Type 'delete' in the field below to confirm.",
                    "Delete",
                    "delete"
                ) { deleted ->
                    if (deleted) {
                        // Published as the console's own producer (UCA-1152): the aggregator
                        // resolves it from the token and authorizes the user through AVP. The
                        // partial's original producer is not named, so removing another
                        // producer's partial needs the console producer to allow that partial.
                        try {
                            aggregator.clientApi.postPartialContentUpdate(
                                partialRemoveUpdate(partial, aggregator.context.viewer.email, Clock.System.now())
                            )
                            onDelete?.invoke()
                        } catch (e: CancellationException) {
                            throw e
                        } catch (e: Throwable) {
                            val error = intakeErrorMessage("remove this partial", e)
                            aggregator.context.showError(error.message, error.description)
                        }
                    }
                    deleting = false
                }

            }
        }
    })
}

/**
 * Build the REMOVE event for a stored partial.
 *
 * The timestamp must be the time of the removal, not the partial's own timestamp. The aggregator
 * republishes the merged entity with the partial's timestamp, and the catalog drops an event older
 * than the entity it holds. A REMOVE that reused the partial's original timestamp was dropped as
 * out of order whenever the base entity had been updated since the partial was added.
 *
 * Published as the console's own producer (UCA-1152), so `producer` is cleared.
 */
internal fun partialRemoveUpdate(partial: PartialUpdate, viewerEmail: String?, now: Instant): PartialUpdate =
    partial.copy(
        ContentUpdateType.REMOVE,
        timestamp = now,
        producer = null,
        auditContext = viewerEmail?.let { AuditContext(it) }
    )
